import { ContentDataProvider } from "../../apis/stateful-content";

export interface Translations {
    [key: string]: string | Translations;
}

export interface TranslateFn {
    (key: string): string;
}

interface TranslationProviderType {
    translations: Translations;
    t: TranslateFn;
}

class TranslationProvider implements TranslationProviderType {
    private readonly _translations: Translations;

    constructor(translations: Translations) {
        this._translations = translations;
        this.t = this.t.bind(this);
    }

    public get translations(): Translations {
        return this._translations;
    }

    public t(key: string): string {
        let node: Translations | string = this.translations;
        return key.split(".").reduce<string>((prevValue, currentValue) => {
            if (prevValue !== "" || node === undefined) {
                return key;
            }
            if (typeof node[currentValue] === "string") {
                return node[currentValue];
            }
            node = node[currentValue];
            return prevValue;
        }, "");
    }
}

const getTranslationProvider = (
    data: Translations | string
): TranslationProviderType => {
    const translations: Translations =
        typeof data === "string" ? JSON.parse(data) : data;
    return new TranslationProvider(translations);
};

export const getTranslateFn = (data: Translations | string): TranslateFn => {
    if (data === undefined || data === "") {
        return (key) => key;
    }
    return getTranslationProvider(data).t;
};

/**
 * Helper function which handles translation script tags and returns the content
 * @param scope
 * @param domNode
 * @param renderType
 * @returns
 */
export const getTranslationSource = (scope, domNode, renderType) => {
    const tagGlobal: HTMLScriptElement = document.querySelector(
        `script[data-ux-i18n-namespace="${scope}"][type="application/json"]`
    );
    const tagComponent: HTMLScriptElement = domNode.querySelector(
        `script[slot="translations"][type="application/json"]`
    );
    const tagOverviewComponent: HTMLScriptElement = domNode
        .closest("ux-iws-stateful-overview")
        ?.querySelector(`script[slot="translations"][type="application/json"]`);
    const content =
        tagGlobal?.innerHTML ||
        tagComponent?.innerHTML ||
        tagOverviewComponent?.innerHTML;
    if (content !== undefined) {
        try {
            const parsedTranslations = JSON.parse(content);
            const parsedTranslationsType =
                tagGlobal !== null
                    ? parsedTranslations[renderType]
                    : parsedTranslations;
            if (parsedTranslationsType !== undefined) {
                return parsedTranslationsType;
            } else {
                console.warn(
                    `translations: '${renderType}' not found in JSON for scope '${scope}'`
                );
            }
        } catch (error) {
            console.warn(
                `translations: Error while parsing JSON for scope '${scope}'. Error message: ${
                    error.message
                }. Content: ${content.slice(0, 200)}...`
            );
        }
    }
};

export enum TranslationRenderType {
    Filter = "filter",
    Content = "content",
}

/**
 * if there's enough space vertically, more items are loaded initially
 **/
export const detectLoadMorePage = (dataProvider: ContentDataProvider) => {
    const scrollHeight = document.documentElement.scrollHeight;
    const windowHeight = window.innerHeight;
    if (scrollHeight <= windowHeight) {
        setTimeout(() => {
            dataProvider.loadMore();
        }, 500);
    }
};

/**
 * if there's enough space vertically inside a modal, more items are loaded initially
 **/
export const detectLoadMoreModal = (dataProvider: ContentDataProvider) => {
    const modal = document.querySelector(".ux-modal--opened") as HTMLElement;
    if (modal === null) return;
    const modalScrollHeight =
        modal.querySelector(".ux-modal-content").scrollHeight;
    const modalHeight = modal.offsetHeight;
    if (modalScrollHeight <= modalHeight) {
        setTimeout(() => {
            dataProvider.loadMore();
        }, 500);
    }
};
